import { getTFunc } from '@icp/i18n';
import { isEqual } from 'lodash-es';
import { toMap } from '@icp/utils';
import { getRowData } from '../EditableTableElement/utils';

export const DISPLAYED_GROUPS_KEY = 'PERMISSION_GROUPS_BATCH_EDIT_DISPLAYED_GROUPS';
export const GROUP_PREFIX = 'group_';

export const ModifiedType = {
  ADD: 'add',
  REMOVE: 'remove',
};

// itemList 中的 item 包含 name 和 token 属性
function getDuplicatedNameItemsMap(itemList) {
  const nameMap = {};
  const duplicatedMap = {};
  itemList.forEach((item) => {
    if (nameMap[item.name]) {
      duplicatedMap[item.token] = item.name;
      duplicatedMap[nameMap[item.name]] = item.name;
    } else {
      nameMap[item.name] = item.token;
    }
  });
  return duplicatedMap;
}

// 找出同一个 target 下面权限名字重复的 permissions
function getDuplicatedNamePermissionsMap(permissions) {
  const targetPermissionsMap = {};
  let duplicatedMap = {};
  permissions.forEach((permission) => {
    const { targetToken } = permission;
    const targetPermissionsItem = targetPermissionsMap[targetToken] || [];
    targetPermissionsItem.push(permission);
    targetPermissionsMap[targetToken] = targetPermissionsItem;
  });
  Object.values(targetPermissionsMap).forEach((targetPermissionsItemValue) => {
    const itemDuplicatedMap = getDuplicatedNameItemsMap(targetPermissionsItemValue);
    duplicatedMap = { ...duplicatedMap, itemDuplicatedMap };
  });
  return duplicatedMap;
}

export function getSelectedGroupTokens(permissionGroups) {
  return (
    JSON.parse(localStorage.getItem(DISPLAYED_GROUPS_KEY)) ||
    permissionGroups.slice(0, 3).map((group) => group.token)
  );
}

function getSelectedGroupPermissions(permissionGroups) {
  const selectedTokenSet = new Set(getSelectedGroupTokens());
  const selectedGroups = permissionGroups.filter((group) => selectedTokenSet.has(group.token));
  return selectedGroups.map((group) => group.permissions);
}

function addGroupsToRowData(rowData, permissionGroups) {
  const permissionGroupsMap = {};
  if (permissionGroups && permissionGroups.length > 0) {
    permissionGroups.forEach((group) => {
      group.permissions.forEach((permission) => {
        const relevantGroupsTokens = permissionGroupsMap[permission.token] || [];
        permissionGroupsMap[permission.token] = relevantGroupsTokens.concat(group.token);
      });
    });
    rowData.forEach((itemData) => {
      const relevantGroupsTokenSet = new Set(permissionGroupsMap[itemData.key]);
      permissionGroups.forEach((group) => {
        itemData[`${GROUP_PREFIX}${group.token}`] = relevantGroupsTokenSet.has(group.token);
      });
    });
  }
}

export function buildRowData({ permissions, permissionGroups, pbcList, readonly }) {
  const needShowPbcTokenMap = getDuplicatedNameItemsMap(pbcList);
  const needShowPermissionTokenMap = getDuplicatedNamePermissionsMap(permissions);
  const permissionList = readonly ? getSelectedGroupPermissions(permissionGroups) : permissions;
  const rowData = permissionList.map((permission) => {
    const { pbcToken, targetToken, token, name } = permission;
    const pbcName = pbcList.find((pbc) => pbc.token === pbcToken)?.name;
    const pbcLabel = pbcName + (needShowPbcTokenMap[pbcToken] ? `[${pbcToken}]` : '');
    const label = name + (needShowPermissionTokenMap[token] ? `[${token}]` : '');
    const treeDataKeyPath = [pbcLabel, targetToken, label];
    return { ...permission, pbcLabel, label, treeDataKeyPath, key: token };
  });
  addGroupsToRowData(rowData, permissionGroups);
  return rowData;
}

export function getInitialColumnDefs() {
  const t = getTFunc('icp-form-renderer');
  return [
    {
      hide: true,
      colId: 'pbcToken',
      field: 'pbcToken',
      headerName: 'PBC Token',
    },
    {
      hide: true,
      colId: 'targetToken',
      field: 'targetToken',
      headerName: 'Target Token',
    },
    {
      hide: true,
      colId: 'token',
      field: 'token',
      headerName: 'Token',
    },
    {
      hide: true,
      colId: 'name',
      field: 'name',
      headerName: t('permission-groups-batch-editor.name'),
    },
    {
      hide: true,
      colId: 'method',
      field: 'method',
      headerName: t('permission-groups-batch-editor.method'),
    },
    {
      hide: true,
      colId: 'customScope',
      field: 'customScope',
      headerName: t('permission-groups-batch-editor.scope'),
    },
  ];
}

export function getTableChangedData(gridApi, oldRowData) {
  const changedData = {
    inserted: [],
    updated: [],
    deleted: [],
    oldValues: [],
  };

  const rowData = getRowData(gridApi);

  if (!Array.isArray(oldRowData)) {
    oldRowData = [];
  }

  // map 加速查找, 没有 id 的行不会生成 map
  const rowDataMap = toMap()(rowData.filter((item) => item.id));
  const oldRowDataMap = toMap()(oldRowData.filter((item) => item.id));

  for (const item of rowData) {
    // inserted
    const oldItem = oldRowDataMap[item.id];
    if (!item.id || !oldItem) {
      changedData.inserted.push(item);
      continue;
    }

    // find updated
    if (oldItem && !isEqual(item, oldItem)) {
      changedData.updated.push(item);
      changedData.oldValues[item.id] = oldItem;
    }
  }

  // deleted
  for (const oldItem of oldRowData) {
    if (oldItem.id && !rowDataMap[oldItem.id]) {
      changedData.deleted.push({ id: oldItem.id });
    }
  }

  return changedData;
}

export function getModification({
  permissionGroups,
  updatedData,
  groupPermissionsMap,
  permissionsMap,
}) {
  const differences = [];
  const modifiedGroups = permissionGroups
    .map((group) => {
      const groupKey = `${GROUP_PREFIX}${group.token}`;
      const groupPermissionSet = new Set(groupPermissionsMap[groupKey] || []);
      let permissionTokens = group.permissions.map((perm) => perm.token);
      const groupDiff = { id: groupKey, name: group.name, modifiedItems: [] };
      updatedData.forEach((itemData) => {
        const wasIncluded = groupPermissionSet.has(itemData.key);
        const isIncluded = itemData[groupKey];
        const diffItem = { ...permissionsMap[itemData.key], group: group.name };
        if (isIncluded && !wasIncluded) {
          groupDiff.modifiedItems.push({ ...diffItem, modifiedType: ModifiedType.ADD });
          permissionTokens = permissionTokens.concat(itemData.key);
        } else if (!isIncluded && wasIncluded) {
          groupDiff.modifiedItems.push({ ...diffItem, modifiedType: ModifiedType.REMOVE });
          permissionTokens = permissionTokens.filter((token) => token !== itemData.key);
        }
      });
      if (groupDiff.modifiedItems.length === 0) {
        return null;
      }
      differences.push(groupDiff);
      return { ...group, permissions: permissionTokens.map((token) => permissionsMap[token]) };
    })
    .filter(Boolean);
  return { modifiedGroups, differences };
}
